import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface CallPlaybackButtonProps {
  callEventId: string;
  shopId: string;
  durationSeconds?: number;
  className?: string;
}

type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'expired' | 'unavailable' | 'error';

export function CallPlaybackButton({
  callEventId,
  shopId,
  durationSeconds,
  className,
}: CallPlaybackButtonProps) {
  const [state, setState] = useState<PlaybackState>('idle');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const fetchAndPlay = async () => {
    setState('loading');
    try {
      const { data, error } = await supabase.functions.invoke('get-call-recording-url', {
        body: { call_event_id: callEventId, shop_id: shopId },
      });

      if (error) {
        console.error('[CallPlaybackButton] Error:', error);
        setState('error');
        return;
      }

      if (data?.status === 'expired') {
        setState('expired');
        return;
      }

      if (!data?.url) {
        setState('unavailable');
        return;
      }

      setAudioUrl(data.url);
      setState('playing');

      // Play via audio element
      if (audioRef.current) {
        audioRef.current.src = data.url;
        audioRef.current.play();
      }
    } catch {
      setState('error');
    }
  };

  const togglePlay = () => {
    if (state === 'idle' || state === 'error') {
      fetchAndPlay();
      return;
    }

    if (state === 'playing' && audioRef.current) {
      audioRef.current.pause();
      setState('paused');
      return;
    }

    if (state === 'paused' && audioRef.current) {
      audioRef.current.play();
      setState('playing');
      return;
    }
  };

  const handleEnded = () => setState('idle');

  if (state === 'expired') {
    return (
      <span className="text-xs text-muted-foreground italic flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        Recording expired
      </span>
    );
  }

  if (state === 'unavailable') {
    return (
      <span className="text-xs text-muted-foreground italic flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        Recording unavailable
      </span>
    );
  }

  const durationLabel = durationSeconds
    ? `${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, '0')}`
    : null;

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={togglePlay}
        disabled={state === 'loading'}
        title={state === 'playing' ? 'Pause' : 'Play recording'}
      >
        {state === 'loading' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : state === 'playing' ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5" />
        )}
      </Button>
      {durationLabel && (
        <span className="text-xs text-muted-foreground">{durationLabel}</span>
      )}
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleEnded}
        onError={() => setState('error')}
        className="hidden"
      />
    </div>
  );
}
